// Localização: frontend/src/pages/Profissionais/ProfissionalProntuarioPage.jsx (versão 1.0.0)
// Página principal do Prontuário do Profissional

import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import Title from '../../components/Layout/Title';
import GoBackButton from '../../components/Common/GoBackButton';
import Alert from '../../components/UI/Alert';
import ProfissionalHeader from '../../components/Prontuario/ProfissionalHeader';
import ProfissionalProntuarioTabs from '../../components/Prontuario/ProfissionalProntuarioTabs';
import { getProntuarioProfissionalSummary } from '../../api/api';

/**
 * Página principal do Prontuário do Profissional
 * 
 * Exibe informações completas do profissional, incluindo:
 * - Cabeçalho com dados básicos e KPIs
 * - Informações pessoais e profissionais
 * - Documentos e certificações
 */
const ProfissionalProntuarioPage = () => {
    const { profissionalid } = useParams();
    const profissionalId = parseInt(profissionalid, 10);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [prontuarioData, setProntuarioData] = useState(null);
    const [refreshCounter, setRefreshCounter] = useState(0);

    /**
     * Reseta o scroll para o topo da página quando a página é montada ou o profissionalId muda
     */
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [profissionalId]);

    /**
     * Carrega os dados do prontuário do profissional
     */
    const fetchProntuarioData = useCallback(async () => {
        if (isNaN(profissionalId) || profissionalId <= 0) {
            setError('ID do profissional inválido.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await getProntuarioProfissionalSummary(profissionalId);
            if (response.data.success) {
                setProntuarioData(response.data.data);
            } else {
                setError(response.data.message || 'Erro ao carregar prontuário do profissional.');
            }
        } catch (err) {
            console.error('Erro ao buscar prontuário do profissional:', err);
            setError(err.response?.data?.message || 'Não foi possível carregar o prontuário do profissional.');
        } finally {
            setLoading(false);
        }
    }, [profissionalId]);

    useEffect(() => {
        fetchProntuarioData();
    }, [fetchProntuarioData, refreshCounter]);

    /**
     * Força refresh dos dados
     */
    const handleRefresh = useCallback(() => {
        setRefreshCounter(prev => prev + 1);
    }, []);

    // Validação do ID do profissional
    if (isNaN(profissionalId) || profissionalId <= 0) {
        return (
            <DashboardLayout>
                <div className="p-4 md:p-6 max-w-7xl mx-auto">
                    <Title text="Prontuário do Profissional" />
                    <div className="mt-4">
                        <GoBackButton />
                        <Alert
                            type="error"
                            message="Erro: ID do profissional não encontrado ou inválido na URL."
                        />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (loading) {
        return (
            <DashboardLayout>
                <div className="p-4 md:p-6 max-w-7xl mx-auto">
                    <Title text="Prontuário do Profissional" />
                    <div className="mt-4">
                        <GoBackButton />
                        <div className="mt-4 text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                            <p className="mt-2 text-gray-600">Carregando prontuário...</p>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (error && !prontuarioData) {
        return (
            <DashboardLayout>
                <div className="p-4 md:p-6 max-w-7xl mx-auto">
                    <Title text="Prontuário do Profissional" />
                    <div className="mt-4">
                        <GoBackButton />
                        <Alert type="error" message={error} />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <div className="w-full min-w-0 overflow-x-hidden p-4 md:p-6">
            <div className="max-w-7xl mx-auto w-full min-w-0">
                {/* Cabeçalho com Informações do Profissional e KPIs */}
                <div className="mb-2 sm:mb-3 min-w-0">
                    <ProfissionalHeader
                        key={`header-${refreshCounter}`}
                        profissionalId={profissionalId}
                        prontuarioData={prontuarioData}
                        onRefresh={handleRefresh}
                    />
                </div>

                {/* Área de Navegação por Abas e Conteúdo */}
                <div className="mt-2 sm:mt-3 min-w-0 w-full">
                    {prontuarioData ? (
                        <ProfissionalProntuarioTabs
                            key={`tabs-${refreshCounter}`}
                            profissionalId={profissionalId}
                            prontuarioData={prontuarioData}
                            onRefresh={handleRefresh}
                        />
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                            <Alert
                                type="info"
                                message="Prontuário ainda não foi criado para este profissional. Entre em contato com o administrador."
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfissionalProntuarioPage;

